// Isolated HTTP client for the Track 2 agentic backend (Azure Function App).
// Rules:
//   - NEVER import or call this file from src/resources/api.resource.ts (T1 chat backend).
//   - NEVER add credentials:'include' or x-csrf-token — the agentic backend uses function-key auth.
//   - On 401 this client surfaces a scoped toast; it does NOT dispatch SESSION_EXPIRED_EVENT
//     and does NOT call /api/v1/auth/refresh.
//   - Dispatches the 'api-activity' window event so Track 1's inactivity timer stays reset
//     while the user is actively using agentic features.
import { toast } from 'react-toastify';
import { agenticMsalInstance, agenticLoginRequest } from '../config/agenticMsalConfig';

const API_ACTIVITY_EVENT = 'api-activity';

const getEnv = (key) => window._env_?.[key] ?? process.env[key];

const getBaseUrl = () => getEnv('REACT_APP_AGENTIC_API_BASE_URL') || '';

const getBaseHeaders = () => {
  const headers = { 'Content-Type': 'application/json' };
  const functionKey = getEnv('REACT_APP_AGENTIC_API_FUNCTION_KEY');
  if (functionKey) headers['x-functions-key'] = functionKey;
  return headers;
};

const appendCode = (url) => {
  const code = getEnv('REACT_APP_AGENTIC_API_CODE');
  if (!code) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}code=${encodeURIComponent(code)}`;
};

const attachBearerToken = async (headers) => {
  try {
    const account = agenticMsalInstance.getActiveAccount();
    if (!account) return headers;
    const tokenResp = await agenticMsalInstance.acquireTokenSilent({
      ...agenticLoginRequest,
      account,
    });
    return { ...headers, Authorization: `Bearer ${tokenResp.accessToken}` };
  } catch {
    return headers;
  }
};

const agenticFetch = async (path, options = {}) => {
  window.dispatchEvent(new Event(API_ACTIVITY_EVENT));

  const baseUrl = getBaseUrl();
  const url = appendCode(`${baseUrl}${path}`);
  const headers = await attachBearerToken({ ...getBaseHeaders(), ...(options.headers || {}) });

  const response = await fetch(url, { ...options, headers });

  if (response.status === 401) {
    toast.error('Agentic session expired. Please log in again.');
    throw new Error('Agentic API: 401 Unauthorized');
  }

  if (!response.ok) {
    throw new Error(`Agentic API error: ${response.status} ${response.statusText}`);
  }

  const text = await response.text();
  return text ? JSON.parse(text) : null;
};

export const agenticGet = (path, options = {}) =>
  agenticFetch(path, { ...options, method: 'GET' });

export const agenticPost = (path, body, options = {}) =>
  agenticFetch(path, {
    ...options,
    method: 'POST',
    body: JSON.stringify(body),
  });

export const agenticPut = (path, body, options = {}) =>
  agenticFetch(path, {
    ...options,
    method: 'PUT',
    body: JSON.stringify(body),
  });

export const agenticDelete = (path, options = {}) =>
  agenticFetch(path, { ...options, method: 'DELETE' });

export default agenticFetch;
